import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ChartErrorBoundary } from '@/components/chart-error-boundary/ChartErrorBoundary';
import { BudgetDeviationBadge } from '@/components/reports/budget-deviation-badge/BudgetDeviationBadge';
import { useMonthlyBudgetAnalytics } from '@/hooks/useMonthlyBudgetAnalytics';
import { ReportProps } from '../common.props';
import { formatNumber } from '@/lib/currency.utils';
import { useMemo } from 'react';
import { BudgetCategoryGroup, BudgetCategoryRow } from '../BudgetCategoryRow';

function MonthlyBudgetContent({ displayCurrency, convert }: ReportProps) {
  const { data, isLoading } = useMonthlyBudgetAnalytics();

  const groupedRows = useMemo(() => {
    const groups: Record<string, BudgetCategoryGroup> = {};
    const rows = data?.rows ?? [];
    for (const row of rows) {
      if (!groups[row.categoryId]) {
        groups[row.categoryId] = {
          categoryId: row.categoryId,
          categoryName: row.categoryName,
          budgeted: 0,
          actual: 0,
          deviation: 0,
          subCategories: [],
        };
      }
      
      const budgetedConverted = convert(row.budgeted, row.currency, displayCurrency);
      const actualConverted = convert(row.actual, row.currency, displayCurrency);
      const deviationConverted = convert(row.deviation, row.currency, displayCurrency);

      groups[row.categoryId].budgeted += budgetedConverted;
      groups[row.categoryId].actual += actualConverted;
      groups[row.categoryId].deviation += deviationConverted;
      groups[row.categoryId].subCategories.push(row);
    }
    return Object.values(groups);
  }, [data?.rows, convert, displayCurrency]);

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading...</p>;
  }

  if (data === null || data === undefined) {
    return (
      <p className="text-sm text-muted-foreground">
        No monthly budget found.{' '}
        <Link to="/budgets" className="underline text-primary">
          Create monthly budget
        </Link>
      </p>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Category</TableHead>
          <TableHead>Currency</TableHead>
          <TableHead className="text-right">Budgeted</TableHead>
          <TableHead className="text-right">Actual</TableHead>
          <TableHead className="text-right">Deviation</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <TableRow>
          <TableCell className="font-semibold">Total</TableCell>
          <TableCell className="font-semibold">{displayCurrency}</TableCell>
          <TableCell className="text-right">{formatNumber(data.totalBudgeted)}</TableCell>
          <TableCell className="text-right">{formatNumber(data.totalActual)}</TableCell>
          <TableCell className="text-right">
            <BudgetDeviationBadge deviation={data.totalDeviation} />
          </TableCell>
        </TableRow>
        {groupedRows.map((group) => (
          <BudgetCategoryRow
            key={group.categoryId}
            group={group}
            displayCurrency={displayCurrency}
            convert={convert}
          />
        ))}
      </TableBody>
    </Table>
  );
}

export function MonthlyBudgetPanel({ displayCurrency, convert }: ReportProps) {
  return (
    <ChartErrorBoundary fallbackTitle="Monthly Budget">
      <Card>
        <CardHeader>
          <CardTitle>Monthly Budget vs Actual</CardTitle>
        </CardHeader>
        <CardContent>
          <MonthlyBudgetContent displayCurrency={displayCurrency} convert={convert} />
        </CardContent>
      </Card>
    </ChartErrorBoundary>
  );
}
